import React, { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap,
  Clock,
  DollarSign,
  Bot,
  Activity,
  Calendar,
  Loader2,
  X,
} from "lucide-react";

interface AgentUsageSheetProps {
  isOpen: boolean;
  onClose: () => void;
  personaKey: string | null;
  orgId?: string;
  personalWorkspaceId?: string;
}

export function AgentUsageSheet({
  isOpen,
  onClose,
  personaKey,
  orgId,
  personalWorkspaceId,
}: AgentUsageSheetProps) {
  const [days, setDays] = useState(30);

  const persona = personaKey ? WORKFORCE_PERSONAS[personaKey] : null;
  const theme = personaKey ? getPersonaTheme(personaKey) : null;

  const usageHistory = useQuery(
    api.aiWorkforce.roiMetrics.getAgentUsageHistory,
    isOpen && personaKey
      ? {
          personaKey,
          orgId: orgId as any,
          personalWorkspaceId: personalWorkspaceId as any,
          days,
        }
      : "skip"
  );

  if (!persona) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden bg-white">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 flex-shrink-0 bg-slate-50/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <div
                className={`w-12 h-12 rounded-2xl ${theme?.bgLight} border ${theme?.border} ${theme?.text} flex items-center justify-center flex-shrink-0 shadow-xs`}
              >
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>{persona.name}</span>
                  <Badge variant="outline" className="text-xs font-semibold py-0.5 text-blue-700 bg-blue-50 border-blue-200">
                    {persona.title}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-xs text-gray-500 mt-0.5">
                  Productivity, task spend, and execution audit log.
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200">
              <button
                onClick={() => setDays(7)}
                className={`px-2.5 py-1 text-2xs font-semibold rounded-lg transition-all ${
                  days === 7 ? "bg-blue-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                7d
              </button>
              <button
                onClick={() => setDays(30)}
                className={`px-2.5 py-1 text-2xs font-semibold rounded-lg transition-all ${
                  days === 30 ? "bg-blue-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                30d
              </button>
              <button
                onClick={() => setDays(90)}
                className={`px-2.5 py-1 text-2xs font-semibold rounded-lg transition-all ${
                  days === 90 ? "bg-blue-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                90d
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {usageHistory === undefined ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mb-2" />
              <p className="text-xs font-medium">Loading {persona.name}'s usage history...</p>
            </div>
          ) : (
            <>
              {/* Quick Stat Tiles */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-50 border border-gray-200">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    <span>Credits Used</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {usageHistory.totalCredits} <span className="text-2xs font-normal text-gray-400">pts</span>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-emerald-50/50 border border-emerald-200/80">
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                    <Clock className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Hours Saved</span>
                  </div>
                  <div className="text-xl font-bold text-emerald-800 mt-1">
                    {usageHistory.estimatedHoursSaved} <span className="text-2xs font-normal text-gray-400">hrs</span>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-blue-50/50 border border-blue-200/80">
                  <div className="flex items-center gap-1.5 text-xs text-blue-700 font-medium">
                    <DollarSign className="w-3.5 h-3.5 text-blue-600" />
                    <span>Labor Value</span>
                  </div>
                  <div className="text-xl font-bold text-blue-800 mt-1">
                    ${usageHistory.estimatedValueSaved}
                  </div>
                </div>
              </div>

              {/* Granular Task Execution Logs */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Recent Task Logs ({usageHistory.logs.length})
                </h4>

                {usageHistory.logs.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm bg-slate-50/50 rounded-xl border border-dashed border-gray-200">
                    No task executions recorded for {persona.name} in the selected period.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                    {usageHistory.logs.map((log: any) => (
                      <div
                        key={log._id}
                        className="p-3 bg-white flex items-center justify-between text-xs hover:bg-slate-50 transition-colors"
                      >
                        <div className="space-y-0.5">
                          <span className="font-semibold text-gray-800 block">
                            {log.operationType.replace(/_/g, " ")}
                          </span>
                          <span className="text-2xs text-gray-400">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-2xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                            -{log.cost} pts
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
